import { useRef } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import IconButton from '@g17eco/molecules/button/IconButton';
import { Column } from '../utils';

const ROW_HEIGHT = 40;
const OVER_FIRST_ITEM_INDEX = 0.5;

// End index will get update when dragging over a item
// Moved position could be positive or negative
const getEndIndex = (startIndex: number, movedPosition: number) => {
  const movedPositionOverItem = movedPosition > 0 ? movedPosition - ROW_HEIGHT / 2 : movedPosition + ROW_HEIGHT / 2;
  const newIndex = startIndex + movedPositionOverItem / ROW_HEIGHT;
  if (newIndex <= OVER_FIRST_ITEM_INDEX) {
    return 0;
  }
  return Math.round(newIndex);
};

interface DraggableColumnProps {
  index: number;
  column: Column;
  handleDrop: DraggableEventHandler;
  handleRemoveColumn: (column: Column) => void;
}

const DraggableColumn = ({ index, column, handleDrop, handleRemoveColumn }: DraggableColumnProps) => {
  // The react-draggable library internally uses the deprecated ReactDOM.findDOMNode() API when the nodeRef prop is not provided.
  // for more info: https://github.com/react-grid-layout/react-draggable?tab=readme-ov-file#draggable-props
  const nodeRef = useRef(null);
  return (
    <Draggable
      key={column.code}
      nodeRef={nodeRef}
      position={{ x: 0, y: 0 }}
      bounds='parent'
      axis='y'
      handle='strong'
      onStop={handleDrop}
    >
      <div
        ref={nodeRef}
        id={`draggable-${column.code}`}
        data-index={index}
        className='position-relative d-flex w-100 align-items-center'
        style={{ height: ROW_HEIGHT }}
      >
        <strong className='fa-solid fa-grip-dots-vertical text-ThemeIconSecondary cursor-pointer p-1'></strong>
        <span className='ml-2 pr-3'>{column.name}</span>

        <div className='ml-auto'>
          <IconButton
            tooltip='Remove this column from the show list'
            color='danger'
            onClick={() => handleRemoveColumn(column)}
            icon='fa-light fa-times'
            className='icon-button'
          />
        </div>
      </div>
    </Draggable>
  );
};

interface DraggableColumnsProps {
  columns: Column[];
  updateColumns: (columns: Column[]) => void;
}

const DraggableColumns = ({ columns, updateColumns }: DraggableColumnsProps) => {
  const handleDrop: DraggableEventHandler = (e, data) => {
    const startIndex = Number(data.node.dataset.index);
    const endIndex = getEndIndex(startIndex, data.y);

    const dragColumn = columns[startIndex];
    const newColumns = [...columns];
    newColumns.splice(startIndex, 1);
    newColumns.splice(endIndex, 0, dragColumn);

    updateColumns(newColumns);
  };

  const handleRemoveColumn = (column: Column) => {
    const newColumns = columns.filter((col) => col.code !== column.code);
    updateColumns(newColumns);
  };

  return (
    <div className='position-relative d-flex flex-column'>
      {columns.map((column, index) => (
        <DraggableColumn
          key={column.code}
          index={index}
          column={column}
          handleDrop={handleDrop}
          handleRemoveColumn={handleRemoveColumn}
        />
      ))}
    </div>
  );
};

export default DraggableColumns;
